import { logCoreUsage, PerformanceCoreDatapoint, PerformanceDatapoint } from "owa-analytics";
import type TableView from "owa-mail-list-store/lib/store/schema/TableView";
import type SelectionDirection from "owa-mail-list-store/lib/store/schema/SelectionDirection";
import MailListItemSelectionSource from "owa-mail-store/lib/store/schema/MailListItemSelectionSource";
import { getMailboxInfo } from "owa-mail-mailboxinfo";
import { getDatapointName } from "./isSelectMailItemCore";
import { getSelectMailItemDatapoint, setSelectMailItemDatapoint, setSmiCustomDataRawArguments, startSelectMailItemScenario, } from "./selectMailItemDatapointGetterAndSetter";
import endSelectMailItemDatapoint from "./endSelectMailItemDatapoint";
import { addOfflineStartDataToSelectMailItem } from "./selectMailItemOfflineTelemetry";

let eventTimestamp: number | undefined = undefined;

export function setInteractionTimestamp(timeStamp?: number): void {
    eventTimestamp = timeStamp;
}

export function createSelectMailItemDatapoint(mailListItemSelectionSource: MailListItemSelectionSource | "Deeplink", tableView: TableView | undefined, rowKey: string | undefined, selectionDirection?: SelectionDirection, shouldExpandAllItemParts?: boolean): void {
    const mailboxInfo = getMailboxInfo(tableView);
    
    // Log AbandonedSMI if it started more than 3 seconds ago and has not ended.
    // This is to catch cases where the user selects a mail item but does not complete the action due to long loading times.
    const previousSelectMailItemDatapoint = getSelectMailItemDatapoint();
    if (previousSelectMailItemDatapoint && !previousSelectMailItemDatapoint.hasEnded) {
        const previousStartTime = previousSelectMailItemDatapoint.getStartTime();
        if (previousStartTime) {
            const abandonedDuration = Date.now() - previousStartTime;
            if (abandonedDuration > 3000) {
                logCoreUsage("AbandonedSMI", mailboxInfo, {
                    E2ETimeElapsed: abandonedDuration,
                });
            }
        }
    }
    
    // End the previous SMI datapoint if it did not complete before new selection.
    endSelectMailItemDatapoint("", true /* shouldInvalidate */);
    
    if (mailListItemSelectionSource != "Deeplink" &&
        shouldNotCreateSMI(mailListItemSelectionSource)) {
        return;
    }
    
    const datapointName = getDatapointName(mailListItemSelectionSource, tableView, rowKey, selectionDirection);
    
    if (!datapointName) {
        return;
    }
    
    const options = {
        timeout: 90 * 1000,
        eventTimestamp,
    };
    
    let datapoint;
    
    switch (datapointName) {
        case "SelectMailItem":
            datapoint = new PerformanceCoreDatapoint("SelectMailItem", mailboxInfo, {
                ...options,
                isScorecardEvent: true,
            });
            break;
        case "SelectMailItemRichPreview":
            datapoint = new PerformanceDatapoint("SelectMailItemRichPreview", mailboxInfo, options);
            
            break;
        case "SelectMailItemPopout":
            datapoint = new PerformanceDatapoint("SelectMailItemPopout", mailboxInfo, options);
            
            break;
        case "SelectMailItemCopilotCatchUp":
            datapoint = new PerformanceDatapoint("SelectMailItemCopilotCatchUp", mailboxInfo, options);
            break;
        case "SelectMailItemScheduleSendToast":
            datapoint = new PerformanceDatapoint("SelectMailItemScheduleSendToast", mailboxInfo, options);
            break;
        case "SelectMailItemCopilotResults":
            datapoint = new PerformanceDatapoint("SelectMailItemCopilotResults", mailboxInfo, options);
            break;
        case "SelectMailItemDeeplink":
            datapoint = new PerformanceDatapoint("SelectMailItemDeeplink", mailboxInfo, options);
            break;
        default:
            return;
    }
    if (datapoint.getEventName() === "SelectMailItem") {
        addOfflineStartDataToSelectMailItem(datapoint, mailboxInfo);
    }
    setSelectMailItemDatapoint(datapoint);
    startSelectMailItemScenario(datapoint);
    
    if (datapoint.getEventName() != "SelectMailItemDeeplink" && tableView) {
        setSmiCustomDataRawArguments(mailListItemSelectionSource, tableView, rowKey ?? "", shouldExpandAllItemParts);
    }
}

/*
 * Do not create SMI datapoint for certain selection sources
 * as we do not show reading pane. Only row focus and selection changes.
 */
function shouldNotCreateSMI(mailListItemSelectionSource: MailListItemSelectionSource): boolean {
    return (mailListItemSelectionSource === MailListItemSelectionSource.MailListItemContextMenu ||
        mailListItemSelectionSource === MailListItemSelectionSource.GroupHeaderContextMenu ||
        mailListItemSelectionSource === MailListItemSelectionSource.MailListGroupHeaderEllipsis ||
        mailListItemSelectionSource === MailListItemSelectionSource.MailListGroupHeader);
}
